import { createTheme } from '@mui/material/styles';

// 색상 팔레트 정의
const colorPalettes = {
  default: {
    primary: '#2196F3',
    secondary: '#21CBF3',
  },
  blue: {
    primary: '#1976D2',
    secondary: '#42A5F5',
  },
  green: {
    primary: '#388E3C',
    secondary: '#66BB6A',
  },
  purple: {
    primary: '#7B1FA2',
    secondary: '#BA68C8',
  },
  orange: {
    primary: '#F57C00',
    secondary: '#FFB74D',
  },
  red: {
    primary: '#D32F2F',
    secondary: '#EF5350',
  },
  teal: {
    primary: '#00796B',
    secondary: '#26A69A',
  },
  pink: {
    primary: '#C2185B',
    secondary: '#E91E63',
  },
  indigo: {
    primary: '#303F9F',
    secondary: '#5C6BC0',
  },
  amber: {
    primary: '#FFA000',
    secondary: '#FFC107',
  },
  cyan: {
    primary: '#0097A7',
    secondary: '#00BCD4',
  },
  brown: {
    primary: '#5D4037',
    secondary: '#8D6E63',
  },
};

// 기본 색상
const baseColors = {
  error: '#f44336',
  background: '#ffffff',
  text: {
    primary: '#333333',
    secondary: '#4a4a4a',
  },
};

// 브레이크포인트
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// 테마 모드에 따른 배경색
const getBackgroundColor = (themeMode) => {
  if (themeMode === 'dark') {
    return {
      default: '#121212',
      paper: '#1E1E1E',
    };
  }
  return {
    default: '#f8fafc',
    paper: '#ffffff',
  };
};

// 테마 모드에 따른 텍스트 색상
const getTextColor = (themeMode) => {
  if (themeMode === 'dark') {
    return {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    };
  }
  return {
    primary: '#333333',
    secondary: '#4a4a4a',
  };
};

// 테마 생성 함수
export const createCustomTheme = (themeMode = 'default', colorScheme = 'default') => {
  // themeMode가 'auto'인 경우 시스템 설정 확인
  let actualThemeMode = themeMode;
  if (themeMode === 'auto') {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      actualThemeMode = 'dark';
    } else {
      actualThemeMode = 'light';
    }
  }

  // colorScheme이 'default'이거나 없으면 기본 색상 사용
  const palette = colorPalettes[colorScheme] || colorPalettes.default;
  const backgroundColor = getBackgroundColor(actualThemeMode);
  const textColor = getTextColor(actualThemeMode);

  const theme = createTheme({
    palette: {
      mode: actualThemeMode === 'dark' ? 'dark' : 'light',
      primary: {
        main: palette.primary,
        light: actualThemeMode === 'dark' ? palette.secondary : palette.primary,
        dark: actualThemeMode === 'dark' ? palette.primary : palette.secondary,
        contrastText: '#ffffff',
      },
      secondary: {
        main: palette.secondary,
        light: actualThemeMode === 'dark' ? palette.primary : palette.secondary,
        dark: actualThemeMode === 'dark' ? palette.secondary : palette.primary,
        contrastText: '#ffffff',
      },
      error: {
        main: baseColors.error,
      },
      background: backgroundColor,
      text: textColor,
    },
    typography: {
      fontFamily: [
        'Pretendard',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 2px 4px rgba(0, 0, 0, 0.05)',
      '0px 4px 8px rgba(0, 0, 0, 0.08)',
      '0px 6px 12px rgba(0, 0, 0, 0.10)',
      '0px 8px 16px rgba(0, 0, 0, 0.12)',
      ...Array(20).fill('none'),
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            boxShadow: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            background: `linear-gradient(45deg, ${palette.primary} 30%, ${palette.secondary} 90%)`,
            '&:hover': {
              background: `linear-gradient(45deg, ${palette.primary} 40%, ${palette.secondary} 80%)`,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                transform: 'translateY(-1px)',
                boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
              },
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            '&.MuiAppBar-root': {
              borderRadius: 0,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            backgroundColor: actualThemeMode === 'dark' ? '#1E1E1E' : '#f8fafc',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            overflow: 'hidden',
            marginTop: '8px',
          },
          list: {
            padding: '0',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: '10px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            '&:last-child': {
              borderBottom: 'none',
            },
            '&.Mui-selected': {
              backgroundColor: `${palette.primary}15`,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: `${palette.primary}25`,
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          icon: {
            transition: 'transform 0.3s ease',
            color: textColor.primary,
          },
          select: {
            paddingRight: '32px',
            '&.MuiSelect-select': {
              paddingRight: '32px',
            },
          },
        },
        defaultProps: {
          MenuProps: {
            PaperProps: {
              elevation: 3,
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            transitionDuration: 200,
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          transitionDuration: 200,
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
          .MuiSelect-root.Mui-focused .MuiSelect-icon {
            transform: rotate(180deg);
          }
          
          .MuiMenu-paper {
            animation: dropdownAppear 0.2s ease-out forwards;
          }
          
          @keyframes dropdownAppear {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `,
      },
    },
    breakpoints,
  });

  return theme;
};

// 기본 테마 (기존 theme.js와 호환)
export const getDefaultTheme = () => createCustomTheme('light', 'default');

